import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Form,
  Badge,
  Alert,
  Spinner,
  Modal,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import consultationService from '../../services/consultationService';
import NursingVitalsModal from '../nurse/NursingVitalsModal';
import Icon from '../common/Icons';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../head-doctor/headDoctorCopy';
import { DOCTOR_COPY } from './doctorCopy';
import { HeadDoctorPageHeader } from '../head-doctor/ui';
import ConsultationStatusBadge from './ConsultationStatusBadge';
import {
  isConsultationQueue,
  isConsultationInProgress,
  matchesConsultationListStatusFilter
} from './utils/consultationStatus';
import { formatAppDateTime } from '../../utils/appTimezone';
import { formatWorkAccidentListTitle } from './utils/workAccidentDisplay';
import './ConsultationStatusBadge.css';
import '../head-doctor/HeadDoctorDashboard.css';

const SERVICE_LINE_BADGE = {
  MEDICAL: { label: 'Médecine', bg: 'primary' },
  DENTAL: { label: 'Dentiste', bg: 'info' },
  NURSING: { label: 'Infirmerie', bg: 'secondary' }
};

const HD_SERVICE_LINE = {
  MEDICAL: { label: 'Médecine', tone: 'medical' },
  DENTAL: { label: 'Dentiste', tone: 'dental' },
  NURSING: { label: 'Infirmerie', tone: 'nursing' }
};

const HD_STATUS_FILTER_OPTIONS = [
  { value: 'ALL', copyKey: 'STATUS_ALL' },
  { value: 'QUEUE', copyKey: 'STATUS_QUEUE', tone: 'queue' },
  { value: 'IN_PROGRESS', copyKey: 'STATUS_IN_PROGRESS', tone: 'inprogress' },
  { value: 'COMPLETED', copyKey: 'STATUS_COMPLETED', tone: 'completed' },
  { value: 'CANCELLED', copyKey: 'STATUS_CANCELLED', tone: 'cancelled' }
];

const PHYSICIAN_STATUS_FILTER_OPTIONS = [
  { value: 'ALL', label: 'Tous' },
  { value: 'QUEUE', label: 'File d\'attente', tone: 'queue' },
  { value: 'IN_PROGRESS', label: 'En cours', tone: 'inprogress' },
  { value: 'COMPLETED', label: 'Terminées', tone: 'completed' },
  { value: 'CANCELLED', label: 'Annulées', tone: 'cancelled' }
];

/**
 * Défaut liste : file d’attente pour la supervision chef ;
 * « Tous » pour médecin/dentiste (prises en charge souvent déjà assignées).
 */
function getDefaultStatusFilter(clinicalWorkspace) {
  if (clinicalWorkspace === 'headDoctor' || clinicalWorkspace === 'nurse') {
    return 'QUEUE';
  }
  return 'ALL';
}

function buildClinicalListCopy(clinicalWorkspace) {
  const base = HEAD_DOCTOR_COPY.CONSULTATIONS;
  if (clinicalWorkspace === 'headDoctor') return base;
  if (clinicalWorkspace === 'dentist') {
    return {
      ...base,
      BADGE: DOCTOR_COPY.PHYSICIAN_DASHBOARD.BADGE_DENTIST,
      PAGE_TITLE: 'Consultations dentaires',
      PAGE_SUBTITLE:
        'File d’attente et suivi des prises en charge dentaires sur le site sélectionné en barre supérieure.',
      PRIMARY_ACTION: 'Ouvrir le dossier odontologique',
      ACTION_EDIT: 'Ouvrir le dossier odontologique'
    };
  }
  return {
    ...base,
    BADGE: DOCTOR_COPY.PHYSICIAN_DASHBOARD.BADGE_PHYSICIAN,
    PAGE_TITLE: 'Consultations médicales',
    PAGE_SUBTITLE:
      'File d’attente et suivi des prises en charge sur le site sélectionné en barre supérieure.',
    PRIMARY_ACTION: 'Ouvrir la fiche clinique',
    ACTION_EDIT: 'Ouvrir la fiche clinique'
  };
}

const ConsultationList = ({
  workSiteId = '',
  clinicalWorkspace = 'physician',
  /** Base URL pour `/new` et `/:id/edit` (médecine vs infirmerie). */
  consultationRoutesBase = '/doctor/consultations',
  onConsultationsMutated,
  externalReloadNonce = 0,
  /** Infirmerie · `parametrage` = constantes d’abord ; `consultations` = examen médical du passage. */
  nurseListMode = 'consultations'
}) => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [vitalsConsultation, setVitalsConsultation] = useState(null);
  const [transferConsultation, setTransferConsultation] = useState(null);
  const [transferReason, setTransferReason] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [closeConsultation, setCloseConsultation] = useState(null);
  const [closeNote, setCloseNote] = useState('');
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [actionMenuConsultation, setActionMenuConsultation] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const defaultStatusFilter = useMemo(
    () => getDefaultStatusFilter(clinicalWorkspace),
    [clinicalWorkspace]
  );
  const [filters, setFilters] = useState(() => ({
    patient_name: '',
    status: getDefaultStatusFilter(clinicalWorkspace),
    date_from: '',
    date_to: ''
  }));

  const isNurse = clinicalWorkspace === 'nurse';
  const isParametrage = isNurse && nurseListMode === 'parametrage';
  /** Même shell UX que médecin chef (médecin + dentiste). */
  const useClinicalListShell = !isNurse;
  const listCopy = useMemo(
    () => buildClinicalListCopy(clinicalWorkspace),
    [clinicalWorkspace]
  );

  /**
   * Compteurs sur le jeu chargé (site + patient + dates), indépendamment du filtre statut UI.
   * Sinon, chaque onglet ne voyait que son sous-ensemble API → les autres compteurs restaient à 0.
   */
  const listStats = useMemo(() => {
    const total = consultations.length;
    const queue = consultations.filter(isConsultationQueue).length;
    const inProgress = consultations.filter(isConsultationInProgress).length;
    const completed = consultations.filter((c) => c.status === 'COMPLETED').length;
    const cancelled = consultations.filter((c) => c.status === 'CANCELLED').length;
    return { total, queue, inProgress, completed, cancelled };
  }, [consultations]);

  const displayedConsultations = useMemo(
    () => consultations.filter((c) => matchesConsultationListStatusFilter(c, filters.status)),
    [consultations, filters.status]
  );

  const statusBadgeLabels = useMemo(
    () =>
      useClinicalListShell
        ? {
            queue: listCopy.STATUS_QUEUE,
            queueHint: listCopy.STAT_QUEUE_HINT,
            inProgress: listCopy.STATUS_IN_PROGRESS,
            inProgressHint: listCopy.STAT_IN_PROGRESS_HINT,
            completed: listCopy.STATUS_COMPLETED.replace(/ées$|s$/, 'ée'),
            completedHint: listCopy.STAT_COMPLETED,
            cancelled: listCopy.STATUS_CANCELLED.replace(/ées$|s$/, 'ée'),
            cancelledHint: listCopy.STATUS_CANCELLED
          }
        : undefined,
    [useClinicalListShell, listCopy]
  );

  const getConsultationRowClass = (consultation) => {
    if (isConsultationQueue(consultation)) return 'hd-consult-row--queue';
    if (isConsultationInProgress(consultation)) return 'hd-consult-row--inprogress';
    return undefined;
  };

  const setStatusFilter = (status) => setFilters((prev) => ({ ...prev, status }));

  const resetFilters = () => {
    setFilters({
      patient_name: '',
      status: defaultStatusFilter,
      date_from: '',
      date_to: ''
    });
  };

  const hasActiveFilters =
    Boolean(filters.patient_name) ||
    filters.status !== defaultStatusFilter ||
    Boolean(filters.date_from) ||
    Boolean(filters.date_to);

  const emptyFilterHint = useMemo(() => {
    if (listStats.total === 0) return null;
    if (filters.status === 'ALL') return null;
    const parts = [];
    if (listStats.queue > 0) parts.push(`${listStats.queue} en file`);
    if (listStats.inProgress > 0) parts.push(`${listStats.inProgress} en cours`);
    if (listStats.completed > 0) parts.push(`${listStats.completed} terminée${listStats.completed > 1 ? 's' : ''}`);
    if (listStats.cancelled > 0) parts.push(`${listStats.cancelled} annulée${listStats.cancelled > 1 ? 's' : ''}`);
    if (parts.length === 0) return null;
    return `Aucune fiche dans ce filtre — ${parts.join(' · ')} sur ce site.`;
  }, [filters.status, listStats]);

  const openActionMenu = (event, consultation) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const itemCount =
      consultation.status === 'COMPLETED' || consultation.status === 'PENDING' ? 3 : 2;
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 240,
        menuHeight: estimateActionMenuHeight(itemCount, { withSeparator: true })
      })
    );
    setActionMenuConsultation(consultation);
  };

  const closeActionMenu = () => setActionMenuConsultation(null);

  const loadConsultations = useCallback(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const filterParams = {};
        if (filters.patient_name) filterParams.patient_name = filters.patient_name;
        // Pas de filtre status serveur : QUEUE / IN_PROGRESS / COMPLETED vivent côté client
        // pour que les compteurs des onglets restent cohérents.
        if (filters.date_from) filterParams.date_from = filters.date_from;
        if (filters.date_to) filterParams.date_to = filters.date_to;
        if (workSiteId) filterParams.site_id = workSiteId;

        const response = await consultationService.getConsultations(filterParams);
        if (response.success) {
          setConsultations(response.data || []);
        }
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement des consultations');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [filters.patient_name, filters.date_from, filters.date_to, workSiteId]);

  useEffect(() => {
    if (!workSiteId) {
      setConsultations([]);
      setLoading(false);
      return;
    }
    loadConsultations();
  }, [loadConsultations, workSiteId]);

  useEffect(() => {
    if (!externalReloadNonce || !workSiteId) return;
    loadConsultations();
  }, [externalReloadNonce, loadConsultations, workSiteId]);

  const handleEdit = (c) => {
    navigate(`${consultationRoutesBase}/${c.id}/edit`);
  };

  const openVitalsModal = (c) => {
    setVitalsConsultation(c);
    setShowVitalsModal(true);
  };

  const closeVitalsModal = () => {
    setShowVitalsModal(false);
    setVitalsConsultation(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette consultation ?')) {
      return;
    }

    try {
      await consultationService.deleteConsultation(id, { site_id: workSiteId });
      loadConsultations();
      if (typeof onConsultationsMutated === 'function') onConsultationsMutated();
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette consultation ?')) {
      return;
    }

    try {
      await consultationService.cancelConsultation(id, { site_id: workSiteId });
      loadConsultations();
      if (typeof onConsultationsMutated === 'function') onConsultationsMutated();
    } catch (err) {
      alert(err.message || 'Erreur lors de l\'annulation');
    }
  };

  const handleNurseContinueConsultation = (c) => {
    navigate(`${consultationRoutesBase}/${c.id}/edit?rx=1`);
  };

  const handleTransferToDoctorQueue = async (c) => {
    setTransferConsultation(c);
    setTransferReason('Orientation médicale requise');
  };

  const handleCloseNursingVisit = async (c) => {
    setCloseConsultation(c);
    setCloseNote('');
  };

  const submitTransferToDoctorQueue = async () => {
    if (!transferConsultation?.id) return;
    if (!transferReason.trim()) {
      window.alert('Le motif de transfert est obligatoire.');
      return;
    }
    setTransferSubmitting(true);
    try {
      await consultationService.transferNursingToMedical(transferConsultation.id, {
        reason: transferReason.trim()
      });
      setTransferConsultation(null);
      setTransferReason('');
      loadConsultations();
      if (typeof onConsultationsMutated === 'function') onConsultationsMutated();
      window.alert('Transfert effectué vers la file d’attente médecin.');
    } catch (err) {
      window.alert(err.message || 'Transfert impossible');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const submitCloseNursingVisit = async () => {
    if (!closeConsultation?.id) return;
    setCloseSubmitting(true);
    try {
      await consultationService.closeNursingVisit(closeConsultation.id, {
        closure_note: closeNote,
        ...(closeConsultation.diagnosis ? {} : { diagnosis: 'Pris en charge en infirmerie' })
      });
      setCloseConsultation(null);
      setCloseNote('');
      loadConsultations();
      if (typeof onConsultationsMutated === 'function') onConsultationsMutated();
      window.alert('Prise en charge infirmière clôturée.');
    } catch (err) {
      window.alert(err.message || 'Clôture impossible');
    } finally {
      setCloseSubmitting(false);
    }
  };

  const formatVitalsBrief = (c) => {
    const parts = [];
    if (c.weight_kg != null && String(c.weight_kg) !== '')
      parts.push(`${Number(c.weight_kg)} kg`);
    if (c.height_cm != null && String(c.height_cm) !== '')
      parts.push(`${c.height_cm} cm`);
    if (c.bp_systolic != null && c.bp_diastolic != null)
      parts.push(`TA ${c.bp_systolic}/${c.bp_diastolic}`);
    if (c.temperature_c != null && String(c.temperature_c) !== '')
      parts.push(`${Number(c.temperature_c)} °C`);
    if (c.heart_rate_bpm != null && String(c.heart_rate_bpm) !== '')
      parts.push(`${c.heart_rate_bpm} bpm`);
    return parts.length ? parts.join(' · ') : '—';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatAppDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPrimaryActionLabel = () => {
    if (useClinicalListShell) return listCopy.PRIMARY_ACTION;
    if (isNurse) {
      return isParametrage ? 'Renseigner l’examen clinique' : 'Mettre à jour l’examen clinique';
    }
    return 'Compléter le dossier clinique';
  };

  const renderHdStatusPill = (consultation) => (
    <ConsultationStatusBadge consultation={consultation} labels={statusBadgeLabels} />
  );

  const renderStatusFilterBar = (options, getLabel, ariaLabel = 'Statut') => (
    <div className="co-status-filter-bar" role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const count =
          opt.value === 'ALL'
            ? listStats.total
            : opt.value === 'QUEUE'
              ? listStats.queue
              : opt.value === 'IN_PROGRESS'
                ? listStats.inProgress
                : opt.value === 'COMPLETED'
                  ? listStats.completed
                  : opt.value === 'CANCELLED'
                    ? listStats.cancelled
                    : null;
        return (
          <button
            key={opt.value}
            type="button"
            className={[
              'co-status-filter-btn',
              opt.tone ? `co-status-filter-btn--${opt.tone}` : '',
              filters.status === opt.value ? 'is-active' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setStatusFilter(opt.value)}
          >
            {getLabel(opt)}
            {count != null ? <span className="co-status-filter-btn__count">{count}</span> : null}
          </button>
        );
      })}
    </div>
  );

  const renderHdServiceLine = (line) => {
    const meta = HD_SERVICE_LINE[line || 'MEDICAL'] || HD_SERVICE_LINE.MEDICAL;
    return (
      <span className={`hd-consult-line-pill hd-consult-line-pill--${meta.tone}`}>
        {meta.label}
      </span>
    );
  };

  const listHeader = useClinicalListShell ? (
    <HeadDoctorPageHeader
      badge={listCopy.BADGE}
      title={listCopy.PAGE_TITLE}
      description={listCopy.PAGE_SUBTITLE}
    />
  ) : null;

  return (
    <Container fluid className={`py-4 ${useClinicalListShell ? 'hd-consult-list px-0' : ''}`}>
      {listHeader}

      {useClinicalListShell ? (
        <div className="hd-co-shell">
          <section className="hd-co-stats" aria-label="Synthèse des consultations">
            <article className="hd-co-stat-card">
              <span className="hd-co-stat-card__label">{listCopy.STAT_TOTAL}</span>
              <span className="hd-co-stat-card__value">{loading ? '—' : listStats.total}</span>
              <span className="hd-co-stat-card__hint">{listCopy.SITE_HINT}</span>
            </article>
            <button
              type="button"
              className={`hd-co-stat-card hd-co-stat-card--queue${filters.status === 'QUEUE' ? ' is-active' : ''}`}
              onClick={() => setStatusFilter('QUEUE')}
            >
              <span className="hd-co-stat-card__label">{listCopy.STAT_QUEUE}</span>
              <span className="hd-co-stat-card__value">{loading ? '—' : listStats.queue}</span>
              <span className="hd-co-stat-card__hint">{listCopy.STAT_QUEUE_HINT}</span>
            </button>
            <button
              type="button"
              className={`hd-co-stat-card hd-co-stat-card--inprogress${filters.status === 'IN_PROGRESS' ? ' is-active' : ''}`}
              onClick={() => setStatusFilter('IN_PROGRESS')}
            >
              <span className="hd-co-stat-card__label">{listCopy.STAT_IN_PROGRESS}</span>
              <span className="hd-co-stat-card__value">{loading ? '—' : listStats.inProgress}</span>
              <span className="hd-co-stat-card__hint">{listCopy.STAT_IN_PROGRESS_HINT}</span>
            </button>
            <button
              type="button"
              className={`hd-co-stat-card hd-co-stat-card--success${filters.status === 'COMPLETED' ? ' is-active' : ''}`}
              onClick={() => setStatusFilter('COMPLETED')}
            >
              <span className="hd-co-stat-card__label">{listCopy.STAT_COMPLETED}</span>
              <span className="hd-co-stat-card__value">{loading ? '—' : listStats.completed}</span>
            </button>
          </section>

          <div className="hd-co-toolbar">
            <div className="hd-co-search-wrap">
              <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
              <input
                type="search"
                className="hd-co-search-input"
                placeholder={listCopy.FILTER_PATIENT_PLACEHOLDER}
                value={filters.patient_name}
                onChange={(e) => setFilters({ ...filters, patient_name: e.target.value })}
                aria-label={listCopy.FILTER_PATIENT}
              />
            </div>
            <div className="hd-co-toolbar-end">
              {renderStatusFilterBar(
                HD_STATUS_FILTER_OPTIONS,
                (opt) => listCopy[opt.copyKey],
                listCopy.FILTER_STATUS
              )}
              <button
                type="button"
                className="hd-co-refresh-btn"
                onClick={loadConsultations}
                disabled={loading || !workSiteId}
                aria-label={listCopy.REFRESH_ARIA}
                title={listCopy.REFRESH_ARIA}
              >
                <Icon name="refresh" size={18} aria-hidden />
              </button>
              <div className="hd-co-toolbar-meta">
                <strong>{loading ? '—' : displayedConsultations.length}</strong>
                <span>{listCopy.RESULTS_LABEL}</span>
              </div>
            </div>
          </div>

          <div className="hd-co-toolbar-dates">
            <span className="hd-co-toolbar-dates__label">{listCopy.FILTER_PERIOD}</span>
            <label className="hd-co-date-field">
              <span>{listCopy.FILTER_DATE_FROM}</span>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </label>
            <label className="hd-co-date-field">
              <span>{listCopy.FILTER_DATE_TO}</span>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </label>
            {hasActiveFilters ? (
              <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                {listCopy.FILTER_RESET}
              </button>
            ) : null}
          </div>

          {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

          {!workSiteId ? (
            <div className="hd-co-state hd-co-state--warn">
              <div className="hd-co-state__icon" aria-hidden>
                <Icon name="exclamationTriangle" size={28} />
              </div>
              <h3 className="hd-co-state__title">Site requis</h3>
              <p className="hd-co-state__text mb-0">{listCopy.NO_SITE}</p>
            </div>
          ) : loading ? (
            <div className="hd-co-state" role="status" aria-live="polite">
              <Spinner animation="border" className="hd-co-state__spinner" />
              <p className="hd-co-state__text mb-0">{listCopy.LOADING}</p>
            </div>
          ) : displayedConsultations.length === 0 ? (
            <div className="hd-co-state hd-co-state--empty">
              <div className="hd-co-state__icon" aria-hidden>
                <Icon name="clipboard-list" size={32} />
              </div>
              <h3 className="hd-co-state__title">{listCopy.EMPTY_TITLE}</h3>
              <p className="hd-co-state__text mb-0">{emptyFilterHint || listCopy.EMPTY}</p>
              {emptyFilterHint && filters.status !== 'ALL' ? (
                <button
                  type="button"
                  className="hd-co-row-action mt-3"
                  onClick={() => setStatusFilter('ALL')}
                >
                  Voir toutes les consultations
                </button>
              ) : null}
            </div>
          ) : (
            <div className="hd-co-table-wrap table-responsive">
              <Table hover className="align-middle hd-consult-list__table mb-0">
                <thead>
                  <tr>
                    <th>{listCopy.COL_DATE}</th>
                    <th>{listCopy.COL_PATIENT}</th>
                    <th>{listCopy.COL_LINE}</th>
                    <th>{listCopy.COL_VITALS}</th>
                    <th>{listCopy.COL_AGE}</th>
                    <th>{listCopy.COL_GENDER}</th>
                    <th>{listCopy.COL_DIAGNOSIS}</th>
                    <th>{listCopy.COL_STATUS}</th>
                    <th className="text-end hd-consult-list__col-actions">{listCopy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedConsultations.map((consultation) => (
                    <tr
                      key={consultation.id}
                      className={getConsultationRowClass(consultation)}
                    >
                      <td className="hd-consult-date">{formatDate(consultation.consultation_date)}</td>
                      <td>
                        <div className="hd-consult-patient">
                          <div className="hd-consult-patient__body">
                            <div className="hd-consult-patient__name-row">
                              <strong className="hd-consult-patient__name">
                                {consultation.patient_name}
                              </strong>
                              {consultation.is_work_accident ? (
                                <span
                                  className="hd-consult-at-badge ms-2"
                                  title={formatWorkAccidentListTitle(consultation, DOCTOR_COPY)}
                                >
                                  {DOCTOR_COPY.WORK_ACCIDENT.BADGE_AT}
                                  {consultation.work_accident_severity
                                    ? ` · ${consultation.work_accident_severity}`
                                    : ''}
                                </span>
                              ) : null}
                            </div>
                            {consultation.patient_phone ? (
                              <div className="hd-consult-patient__meta">{consultation.patient_phone}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>{renderHdServiceLine(consultation.service_line)}</td>
                      <td className="hd-consult-vitals">{formatVitalsBrief(consultation)}</td>
                      <td>{consultation.patient_age || '—'}</td>
                      <td>
                        {consultation.patient_gender === 'M'
                          ? 'M'
                          : consultation.patient_gender === 'F'
                            ? 'F'
                            : consultation.patient_gender === 'OTHER'
                              ? 'Autre'
                              : '—'}
                      </td>
                      <td>
                        <div className="hd-consult-diagnosis">
                          {(consultation.diseases?.length
                            ? consultation.diseases.map((d) => d.code)
                            : consultation.disease_code
                              ? consultation.disease_code.split(',').map((c) => c.trim())
                              : []
                          ).map((code) => (
                            <span key={code} className="hd-consult-disease-code">
                              {code}
                            </span>
                          ))}
                          <span className="hd-consult-diagnosis__text" title={consultation.diagnosis || ''}>
                            {consultation.diagnosis || '—'}
                          </span>
                        </div>
                      </td>
                      <td>{renderHdStatusPill(consultation)}</td>
                      <td className="text-end">
                        <div className="d-inline-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="hd-co-row-action"
                            onClick={() => handleEdit(consultation)}
                          >
                            {getPrimaryActionLabel()}
                          </button>
                          <button
                            type="button"
                            className="hd-exam-actions-menu__toggle"
                            onClick={(e) => openActionMenu(e, consultation)}
                            aria-label={`Actions pour ${consultation.patient_name || 'consultation'}`}
                            title="Actions"
                          >
                            <Icon name="ellipsis-vertical" size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white">
            <div>
              <h4 className={`mb-1 ${isParametrage ? 'text-success' : 'text-danger'}`}>
                {isParametrage ? 'Paramètres · constantes vitales' : 'Consultation · examen médical'}
              </h4>
              <p className="text-muted mb-0 small">
                {isParametrage
                  ? 'Enregistrées sur ce passage · visibles dans le dossier adhérent. Effectuez ce volet avant l’examen médical (menu Consultation).'
                  : isNurse
                    ? 'Flux : Accueil du passage → Paramètres (constantes) → cet écran pour l’examen (symptômes, diagnostic, notes).'
                    : 'Gérer les consultations médicales'}
              </p>
            </div>
          </Card.Header>

          <Card.Body>
            <Row className="mb-3 g-2">
              <Col xs={12}>
                <div className="co-list-summary mb-2">
                  <span className="co-list-summary__item">
                    File d&apos;attente <strong>{loading ? '—' : listStats.queue}</strong>
                  </span>
                  <span className="co-list-summary__item">
                    En cours <strong>{loading ? '—' : listStats.inProgress}</strong>
                  </span>
                  <span className="co-list-summary__item">
                    Terminées <strong>{loading ? '—' : listStats.completed}</strong>
                  </span>
                </div>
                {renderStatusFilterBar(
                  PHYSICIAN_STATUS_FILTER_OPTIONS,
                  (opt) => opt.label,
                  'Filtrer par statut'
                )}
              </Col>
            </Row>

            <Row className="mb-3 g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Rechercher un patient</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nom du patient…"
                    value={filters.patient_name}
                    onChange={(e) => setFilters({ ...filters, patient_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Date début</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Date fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button variant="secondary" onClick={resetFilters}>
                  Réinitialiser
                </Button>
              </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {!workSiteId ? (
              <Alert variant="warning" className="mb-0">
                Le site de travail est défini dans la barre supérieure. Sélectionnez-le pour afficher les
                patients en attente et vos dossiers.
              </Alert>
            ) : loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="warning" />
              </div>
            ) : consultations.length === 0 ? (
              <Alert variant="info" className="mb-0">
                Aucune consultation trouvée pour ce site.
              </Alert>
            ) : displayedConsultations.length === 0 ? (
              <Alert variant="info" className="mb-0">
                Aucune consultation ne correspond à ce filtre de statut.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Patient</th>
                      <th>Filière</th>
                      <th>Constantes</th>
                      <th>Âge</th>
                      <th>Genre</th>
                      <th>Diagnostic</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedConsultations.map((consultation) => (
                      <tr key={consultation.id} className={getConsultationRowClass(consultation)}>
                        <td>{formatDate(consultation.consultation_date)}</td>
                        <td>
                          <div>
                            <strong>{consultation.patient_name}</strong>
                            {consultation.is_work_accident ? (
                              <span
                                className="hd-consult-at-badge ms-2"
                                title={formatWorkAccidentListTitle(consultation, DOCTOR_COPY)}
                              >
                                {DOCTOR_COPY.WORK_ACCIDENT.BADGE_AT}
                                {consultation.work_accident_severity
                                  ? ` · ${consultation.work_accident_severity}`
                                  : ''}
                              </span>
                            ) : null}
                            {consultation.patient_phone && (
                              <div className="text-muted small">{consultation.patient_phone}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const line = consultation.service_line || 'MEDICAL';
                            const meta = SERVICE_LINE_BADGE[line] || SERVICE_LINE_BADGE.MEDICAL;
                            return <Badge bg={meta.bg}>{meta.label}</Badge>;
                          })()}
                        </td>
                        <td
                          className={`small ${isParametrage ? 'text-dark' : 'text-muted'}`}
                          style={{ maxWidth: '220px' }}
                        >
                          {formatVitalsBrief(consultation)}
                        </td>
                        <td>{consultation.patient_age || '-'}</td>
                        <td>
                          {consultation.patient_gender === 'M'
                            ? 'M'
                            : consultation.patient_gender === 'F'
                              ? 'F'
                              : consultation.patient_gender === 'OTHER'
                                ? 'Autre'
                                : '-'}
                        </td>
                        <td>
                          <div style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(consultation.diseases?.length
                              ? consultation.diseases.map((d) => d.code)
                              : consultation.disease_code
                                ? consultation.disease_code.split(',').map((c) => c.trim())
                                : []
                            ).map((code) => (
                              <Badge key={code} bg="dark" className="me-1 mb-1 font-monospace">
                                {code}
                              </Badge>
                            ))}
                            <span title={consultation.diagnosis || ''}>
                              {consultation.diagnosis || '—'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <ConsultationStatusBadge consultation={consultation} />
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {isNurse && consultation.status !== 'CANCELLED' && (
                              <Button variant="success" size="sm" onClick={() => openVitalsModal(consultation)}>
                                Constantes vitales
                              </Button>
                            )}
                            {isParametrage && (
                              <Dropdown size="sm">
                                <Dropdown.Toggle
                                  variant="outline-success"
                                  id={`nurse-orientation-${consultation.id}`}
                                  disabled={consultation.status === 'CANCELLED'}
                                >
                                  Conduite clinique
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item
                                    onClick={() => handleNurseContinueConsultation(consultation)}
                                  >
                                    Ouvrir dossier infirmier
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleTransferToDoctorQueue(consultation)}
                                  >
                                    Transférer vers file médecin
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleCloseNursingVisit(consultation)}>
                                    Clôturer prise en charge infirmière
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                            {!isParametrage && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(consultation)}
                              >
                                {getPrimaryActionLabel()}
                              </Button>
                            )}
                            {!isNurse && (
                              <Dropdown as={ButtonGroup} size="sm">
                                <Dropdown.Toggle
                                  variant="outline-danger"
                                  id={`consultation-sensitive-${consultation.id}`}
                                >
                                  Actions sensibles
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  {(consultation.status === 'COMPLETED' ||
                                    consultation.status === 'PENDING') && (
                                    <Dropdown.Item onClick={() => handleCancel(consultation.id)}>
                                      Clôturer sans prise en charge
                                    </Dropdown.Item>
                                  )}
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => handleDelete(consultation.id)}
                                  >
                                    Supprimer définitivement la fiche
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {useClinicalListShell && actionMenuConsultation && (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => {
                closeActionMenu();
                handleEdit(actionMenuConsultation);
              }}
            >
              <Icon name="edit" size={15} />
              <span>{listCopy.ACTION_EDIT}</span>
            </button>
            {(actionMenuConsultation.status === 'COMPLETED' ||
              actionMenuConsultation.status === 'PENDING') && (
              <button
                type="button"
                className="hd-exam-actions-menu__item"
                role="menuitem"
                onClick={() => {
                  closeActionMenu();
                  handleCancel(actionMenuConsultation.id);
                }}
              >
                <Icon name="eyeSlash" size={15} />
                <span>{listCopy.ACTION_CANCEL}</span>
              </button>
            )}
            <div className="hd-exam-actions-menu__separator" />
            <button
              type="button"
              className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
              role="menuitem"
              onClick={() => {
                closeActionMenu();
                handleDelete(actionMenuConsultation.id);
              }}
            >
              <Icon name="trash" size={15} />
              <span>{listCopy.ACTION_DELETE}</span>
            </button>
          </div>
        </>
      )}

      {isNurse && (
        <NursingVitalsModal
          show={showVitalsModal}
          onHide={closeVitalsModal}
          consultation={vitalsConsultation}
          viewerSiteId={workSiteId}
          onSuccess={() => {
            loadConsultations();
            if (typeof onConsultationsMutated === 'function') onConsultationsMutated();
          }}
        />
      )}

      <Modal show={!!transferConsultation} onHide={() => setTransferConsultation(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Transférer vers médecin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Le patient sera retiré de la file infirmerie et envoyé dans la file d&apos;attente médecin.
          </p>
          <Form.Group>
            <Form.Label>Motif de transfert *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
              placeholder="Indiquez la raison clinique du transfert"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTransferConsultation(null)} disabled={transferSubmitting}>
            Annuler
          </Button>
          <Button variant="primary" onClick={submitTransferToDoctorQueue} disabled={transferSubmitting}>
            {transferSubmitting ? <Spinner animation="border" size="sm" /> : 'Confirmer transfert'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!closeConsultation} onHide={() => setCloseConsultation(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clôturer prise en charge infirmière</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Cette action clôture la visite infirmière du patient.
          </p>
          <Form.Group>
            <Form.Label>Note de clôture (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              placeholder="Résumé de fin de prise en charge"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCloseConsultation(null)} disabled={closeSubmitting}>
            Annuler
          </Button>
          <Button variant="success" onClick={submitCloseNursingVisit} disabled={closeSubmitting}>
            {closeSubmitting ? <Spinner animation="border" size="sm" /> : 'Confirmer clôture'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ConsultationList;

